import React from 'react';
import cn from 'classnames';

import Section from 'react-scale/components/elements/section';
import FeatureTitle from 'react-scale/components/pages/open-datasets/FeatureTitle';
import { GridRepeat, GridItem } from 'react-scale/components/website/big/main/Grid';
import MapRoute from 'react-scale/components/website/big/open-datasets/shared/MapRoute';
import VideoHls from 'react-scale/components/website/customers/case/VideoHls';

import { mq } from 'react-scale/components/website/big/Themes';

const DataCollection = ({ overtitle, title, text, iconColors, mapAssets, video, caseColor }) => (
  <Section id="data-collection">
    <FeatureTitle
      iconSVG={<Icon colors={iconColors} />}
      overtitle={overtitle}
      title={title}
      subtitle={text}
      caseColor={caseColor}
    />
    <div className="mt-20">
      <GridRepeat tag="ul" xl={6} lg={6} md={8} justify columns={{ md: 1 }}>
        {mapAssets.map(asset => (
          <GridItem tag="li" empty key={asset.name}>
            <div className={cn('asset-wrapper', video ? 'video' : '')}>
              <div className="asset-position">
                <MapRoute src={asset.src} />
              </div>
            </div>
          </GridItem>
        ))}
        {video && (
          <GridItem tag="li" empty>
            <div className={cn('asset-wrapper', video ? 'video' : '')}>
              <div className="asset-position">
                <VideoHls
                  src={video.url}
                  poster={video.poster}
                  alt={video.title}
                  muted
                  playsinline
                  controls={false}
                  autoPlay
                  loop
                  fullWidth
                  fullHeight
                  blur
                  playStopOnViewportChange
                />
              </div>
            </div>
          </GridItem>
        )}
      </GridRepeat>
    </div>
    <style jsx>{`
      .asset-wrapper {
        background: #000;
        width: 100%;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        position: relative;
      }
      .asset-wrapper:not(.video) {
        height: 400px;
      }
      @media (min-width: ${mq.lg}) {
        .asset-wrapper:not(.video) {
          height: 450px;
        }
      }
      @media (min-width: ${mq.xl}) {
        .asset-wrapper:not(.video) {
          height: 552px;
        }
      }
      .asset-wrapper.video {
        height: 0;
        padding-bottom: 56.25%;
      }

      .asset-position {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
      }
    `}</style>
  </Section>
);

DataCollection.defaultProps = {
  overtitle: { text: 'Data Collection' },
  title: 'Complex Driving Scenarios in Urban Environments',
  video: null
};

const Icon = ({ colors }) => (
  <svg viewBox="0 0 180 180">
    <path
      d="M90 180C139.706 180 180 139.706 180 90C180 40.2944 139.706 0 90 0C40.2944 0 0 40.2944 0 90C0 139.706 40.2944 180 90 180Z"
      fill="black"
    />
    <path
      d="M74.4023 85.5C76.8876 85.5 78.9023 83.4853 78.9023 81C78.9023 78.5147 76.8876 76.5 74.4023 76.5C71.9171 76.5 69.9023 78.5147 69.9023 81C69.9023 83.4853 71.9171 85.5 74.4023 85.5Z"
      fill={colors[0]}
    />
    <path
      d="M74.4023 51C80.2013 51 84.9023 46.299 84.9023 40.5C84.9023 34.701 80.2013 30 74.4023 30C68.6034 30 63.9023 34.701 63.9023 40.5C63.9023 46.299 68.6034 51 74.4023 51Z"
      fill={colors[0]}
    />
    <path
      d="M105.598 85.5C108.083 85.5 110.098 83.4853 110.098 81C110.098 78.5147 108.083 76.5 105.598 76.5C103.112 76.5 101.098 78.5147 101.098 81C101.098 83.4853 103.112 85.5 105.598 85.5Z"
      fill={colors[1]}
    />
    <path
      d="M90 76.5C92.4853 76.5 94.5 74.4853 94.5 72C94.5 69.5147 92.4853 67.5 90 67.5C87.5147 67.5 85.5 69.5147 85.5 72C85.5 74.4853 87.5147 76.5 90 76.5Z"
      fill={colors[2]}
    />
    <path
      d="M90 112.5C92.4853 112.5 94.5 110.485 94.5 108C94.5 105.515 92.4853 103.5 90 103.5C87.5147 103.5 85.5 105.515 85.5 108C85.5 110.485 87.5147 112.5 90 112.5Z"
      fill={colors[2]}
    />
    <path
      d="M74.4023 103.5C76.8876 103.5 78.9023 101.485 78.9023 99C78.9023 96.5147 76.8876 94.5 74.4023 94.5C71.9171 94.5 69.9023 96.5147 69.9023 99C69.9023 101.485 71.9171 103.5 74.4023 103.5Z"
      fill={colors[1]}
    />
    <path
      d="M105.598 103.5C108.083 103.5 110.098 101.485 110.098 99C110.098 96.5147 108.083 94.5 105.598 94.5C103.112 94.5 101.098 96.5147 101.098 99C101.098 101.485 103.112 103.5 105.598 103.5Z"
      fill={colors[0]}
    />
    <path
      d="M74.4023 71.25C78.5445 71.25 81.9023 67.8921 81.9023 63.75C81.9023 59.6079 78.5445 56.25 74.4023 56.25C70.2602 56.25 66.9023 59.6079 66.9023 63.75C66.9023 67.8921 70.2602 71.25 74.4023 71.25Z"
      fill={colors[0]}
    />
    <path
      d="M105.598 150C111.397 150 116.098 145.299 116.098 139.5C116.098 133.701 111.397 129 105.598 129C99.7987 129 95.0977 133.701 95.0977 139.5C95.0977 145.299 99.7987 150 105.598 150Z"
      fill={colors[0]}
    />
    <path
      d="M105.598 123.75C109.74 123.75 113.098 120.392 113.098 116.25C113.098 112.108 109.74 108.75 105.598 108.75C101.456 108.75 98.0977 112.108 98.0977 116.25C98.0977 120.392 101.456 123.75 105.598 123.75Z"
      fill={colors[0]}
    />
    <path
      d="M54.9258 138.75C60.7248 138.75 65.4258 134.049 65.4258 128.25C65.4258 122.451 60.7248 117.75 54.9258 117.75C49.1268 117.75 44.4258 122.451 44.4258 128.25C44.4258 134.049 49.1268 138.75 54.9258 138.75Z"
      fill={colors[2]}
    />
    <path
      d="M75.0586 124.14C79.2007 124.14 82.5586 120.782 82.5586 116.64C82.5586 112.498 79.2007 109.14 75.0586 109.14C70.9165 109.14 67.5586 112.498 67.5586 116.64C67.5586 120.782 70.9165 124.14 75.0586 124.14Z"
      fill={colors[2]}
    />
    <path
      d="M125.063 62.25C130.861 62.25 135.563 57.549 135.563 51.75C135.563 45.951 130.861 41.25 125.063 41.25C119.264 41.25 114.562 45.951 114.562 51.75C114.562 57.549 119.264 62.25 125.063 62.25Z"
      fill={colors[2]}
    />
    <path
      d="M104.941 70.8604C109.084 70.8604 112.441 67.5025 112.441 63.3604C112.441 59.2182 109.084 55.8604 104.941 55.8604C100.799 55.8604 97.4414 59.2182 97.4414 63.3604C97.4414 67.5025 100.799 70.8604 104.941 70.8604Z"
      fill={colors[2]}
    />
    <path
      d="M140.672 111.75C146.471 111.75 151.172 107.049 151.172 101.25C151.172 95.451 146.471 90.75 140.672 90.75C134.873 90.75 130.172 95.451 130.172 101.25C130.172 107.049 134.873 111.75 140.672 111.75Z"
      fill={colors[1]}
    />
    <path
      d="M120.539 97.1103C124.681 97.1103 128.039 93.7525 128.039 89.6104C128.039 85.4682 124.681 82.1104 120.539 82.1104C116.397 82.1104 113.039 85.4682 113.039 89.6104C113.039 93.7525 116.397 97.1103 120.539 97.1103Z"
      fill={colors[1]}
    />
    <path
      d="M39.3281 89.25C45.1271 89.25 49.8281 84.549 49.8281 78.75C49.8281 72.951 45.1271 68.25 39.3281 68.25C33.5291 68.25 28.8281 72.951 28.8281 78.75C28.8281 84.549 33.5291 89.25 39.3281 89.25Z"
      fill={colors[1]}
    />
    <path
      d="M59.4609 97.8897C63.6031 97.8897 66.9609 94.5318 66.9609 90.3896C66.9609 86.2475 63.6031 82.8896 59.4609 82.8896C55.3188 82.8896 51.9609 86.2475 51.9609 90.3896C51.9609 94.5318 55.3188 97.8897 59.4609 97.8897Z"
      fill={colors[1]}
    />
  </svg>
);

export default DataCollection;
